import { IgnitionGame } from '../../games/ignition/IgnitionGame.js';
import { IgnitionVortex } from '../../games/ignition/IgnitionVortex.js';
import { IgnitionRenderer } from '../../games/ignition/IgnitionRenderer.js';
import { Grid } from '../../core/Grid.js';
import { Position, positionKey } from '../../core/GridGeometry.js';
import { RenderConfig } from '../../rendering/Renderer.js';
import { TutorialGameAdapter } from '../TutorialEngine.js';

// ── Ignition-specific types ─────────────────────────────────────

export interface IgnitionRenderOptions {
    // Placeholder — IgnitionRenderer is fully game-state driven
}

// ── Adapter ─────────────────────────────────────────────────────

export class IgnitionTutorialAdapter implements TutorialGameAdapter {
    private game: IgnitionGame;
    private renderer: IgnitionRenderer;

    constructor(canvas: HTMLCanvasElement, grid: Grid) {
        this.game = new IgnitionGame(grid);
        this.renderer = new IgnitionRenderer(canvas, grid);
        // Static scenes: no tweening, so the renderer never self-re-renders and
        // wipes the engine-drawn annotations.
        this.renderer.setSelfAnimation(false);
    }

    applyState(state: unknown): void {
        const serialized = state as string;
        this.game.reset();
        if (serialized) {
            this.game.deserialize(serialized);
        }
    }

    render(_options: unknown): void {
        this.renderer.render(this.game);
    }

    getConfig(): RenderConfig {
        return this.renderer.getConfig();
    }
}

export function createIgnitionTutorialAdapter(
    canvas: HTMLCanvasElement,
    grid: Grid,
): TutorialGameAdapter {
    return new IgnitionTutorialAdapter(canvas, grid);
}

// ── Vortex state ─────────────────────────────────────────────────

export interface IgnitionVortexTutorialState {
    fuses: string;
    gateMode?: boolean;
    /**
     * Extra gate pairs (cell strings, e.g. ['B5', 'D5']) merged on top of the
     * adapter's default teleport pair for this scene only — used by the
     * activation scene to show a second, blocked (amber) pair. Omit otherwise.
     */
    extraGates?: Array<[string, string]>;
    selectedDigit?: number;
}

/** Parse a cell string like "B5" into a Position ({ row: 5, col: 1 }). */
function parseCellString(s: string): Position {
    return { col: s.charCodeAt(0) - 65, row: parseInt(s.slice(1), 10) };
}


// ── Vortex Adapter ───────────────────────────────────────────────

export class IgnitionVortexTutorialAdapter implements TutorialGameAdapter {
    private game: IgnitionVortex;
    private renderer: IgnitionRenderer;
    private gatePairs: Array<[Position, Position]>;
    private allGates: Set<string>;
    private currentGateMode: boolean = false;

    constructor(
        canvas: HTMLCanvasElement,
        grid: Grid,
        gatePairs: Array<[Position, Position]>,
    ) {
        this.game = new IgnitionVortex(grid);
        this.renderer = new IgnitionRenderer(canvas, grid);
        this.renderer.setSelfAnimation(false);   // static scenes — see the Base adapter
        this.gatePairs = gatePairs;
        this.allGates = new Set<string>();
        for (const [a, b] of gatePairs) {
            this.allGates.add(positionKey(a));
            this.allGates.add(positionKey(b));
        }
    }

    applyState(state: unknown): void {
        const s = state as IgnitionVortexTutorialState | null;
        this.game.reset();
        const pairs = [...this.gatePairs];
        for (const [a, b] of s?.extraGates ?? []) {
            pairs.push([parseCellString(a), parseCellString(b)]);
        }
        const allGates = new Set<string>();
        for (const [a, b] of pairs) {
            allGates.add(positionKey(a));
            allGates.add(positionKey(b));
        }
        this.game.setVortexData(pairs, allGates);
        if (s?.fuses) {
            this.game.deserialize(s.fuses);
        }
        if (s?.selectedDigit !== undefined) {
            this.game.toggleSelectedDigit(s.selectedDigit);
        }
        this.currentGateMode = s?.gateMode ?? false;
    }

    render(_options: unknown): void {
        this.renderer.setGateMode(this.currentGateMode);
        this.renderer.render(this.game);
    }

    getConfig(): RenderConfig {
        return this.renderer.getConfig();
    }
}

export function createIgnitionVortexTutorialAdapter(
    canvas: HTMLCanvasElement,
    grid: Grid,
): TutorialGameAdapter {
    // Gate pair for the vortex ignition tutorial: E4 ↔ C5
    const gatePairs: Array<[Position, Position]> = [
        [{ row: 4, col: 4 }, { row: 5, col: 2 }],
    ];
    return new IgnitionVortexTutorialAdapter(canvas, grid, gatePairs);
}
